
import Component, { mixins } from 'vue-class-component';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import { ValidationObserver } from 'vee-validate';
import { Prop } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { RootState } from '@/store/store';
import { PendingCitationResponse } from '@/api/pending-citations.api';
import { Citation } from '@/store/modules/documents/types/citations.types';
import { CitationTypes } from '@/jbi-shared/types/citation.fields';
import { PendingCitationData } from '@/jbi-shared/types/document.types';
import { StagingEditorComputedValuesMixin } from '../../mixins/staging-editor-computed-values.mixin';
import { StagingEditorStateMixin } from '../../mixins/staging-editor-state.mixin';
import ArchiveCitationValidation from './ArchiveCitationValidation.vue';
import StagingCitationForm from '../../../DocumentEditor/components/tab/citations/StagingCitationForm.vue';
import {
  ValidationObserverInstance,
  CitationFormFields,
  CitationContent,
  CompleteCitation
} from '@/jbi-shared/types/citation.dto';
import { CitationAuthor } from '@/jbi-shared/types/citation.fields';

@Component({
  components: {
    BaseModal,
    StagingCitationForm,
    ValidationObserver,
    ArchiveCitationValidation
  }
})
export default class StagingCitationCard extends mixins(
  StagingEditorComputedValuesMixin,
  StagingEditorStateMixin
) {
  private readonly logger = console;
  public draggable = true;

  @Prop(Object) public citation!: PendingCitationData;
  @Prop(Object) public originalCitation?:
    | PendingCitationData
    | Citation
    | PendingCitationResponse; // Original citation data with author array intact
  @Prop(String) public citationReferenceNumber?: string;
  @Prop(Boolean) public activeCitation!: boolean;
  @Prop({ type: Boolean, default: false }) public showArchiveButton!: boolean;
  @Prop({ type: Boolean, default: true }) public showDeleteButton!: boolean;
  public isVisible: boolean = false;

  @State((state: RootState) => state.projects.apiState.editCitation.loading)
  public editCitationLoading!: boolean;

  get CitationTypes() {
    return CitationTypes;
  }

  get getAuthor(): string {
    return this.citation.content.author
      ? this.citation.content.author.split(' ').shift()
      : '';
  }

  get citationHasAuthor(): boolean {
    return !!this.citation.content.author;
  }

  get citationHasIssueDate(): boolean {
    return !!this.citation.content.issueDate;
  }

  get citationHasTitle(): boolean {
    return !!this.citation.content.title;
  }

  get referencedCitationIds() {
    return this.uniqUsedCitationIds.map((id) => parseInt(id));
  }

  /**
   * Get citation data formatted for editing form
   * This uses the original citation data with author array intact so the form can properly detect organization vs individual authors
   */
  get citationForEditing() {
    if (!this.originalCitation) {
      return this.citation;
    }

    // Handle newly added citations (PendingCitationResponse structure)
    if ('citation' in this.originalCitation) {
      return {
        id: this.originalCitation.citationId,
        content: this.originalCitation.citation.content
      };
    }

    // Handle previous citations (Citation structure) - they already have the right format
    if ('content' in this.originalCitation) {
      return {
        id: this.originalCitation.id,
        content: this.originalCitation.content
      };
    }

    // Fallback to the display citation if original structure is not recognized
    return this.citation;
  }

  public saveCitation(citationFields: CitationFormFields) {
    if (!citationFields.type) {
      this.logger.error('Cannot save citation, type is null.', citationFields);
      return;
    }
    const newCitation: CompleteCitation = {
      id: citationFields.id as string,
      content: {
        plainText: citationFields.plainText || '',
        type: citationFields.type,
        title: citationFields.title || '',
        author: citationFields.author
          ? JSON.stringify(citationFields.author)
          : '',
        year: citationFields.year || '',
        journal: citationFields.journal || ''
      }
    };
    this.$emit('editCitation', newCitation);
  }

  public handleVisibilityChange(isVisible: boolean) {
    this.isVisible = isVisible;
  }

  public async handleEditFormSubmit(validator: ValidationObserverInstance) {
    if (this.$refs.CitationFormForEdit) {
      const form = this.$refs.CitationFormForEdit as StagingCitationForm;

      // Use the form's internal validation
      let validated = false;
      try {
        const validationResult = await form.validate();

        // Handle cases where validation returns undefined but form is actually valid
        // Check if the form has required fields filled
        const citationFields = form.transformFieldToCitation();

        const hasRequiredFields =
          citationFields &&
          citationFields.type &&
          (citationFields.title || citationFields.plainText);

        // Validation passes if explicitly true, undefined with valid fields, or valid fields exist
        validated =
          validationResult === true ||
          (validationResult !== false && !!hasRequiredFields);
      } catch (error) {
        validated = false;
      }

      if (!validated) {
        return;
      }

      const citationFields = form.transformFieldToCitation();

      // Use ALL the citation fields from the form, I mistakenly hardcode specific fields
      // Handle author separately due to type conversion needs
      const { author, type, ...restFields } = citationFields;

      const citationContent: Omit<CitationContent, 'author'> & {
        author: string | CitationAuthor[];
      } = {
        // Ensure required fields from CitationContent interface
        type: (type || 'plain') as string,
        plainText: restFields.plainText || '',
        title: restFields.title || '',
        year: restFields.year || '',
        journal: restFields.journal || '',
        // Include all other fields from the form
        ...restFields,
        author: author || ''
      };

      // Create a clean object to avoid any Vue reactivity issues
      const cleanCitationContent = JSON.parse(JSON.stringify(citationContent));

      // Pass the complete citation data
      const completeCitation = {
        id: this.citation.id.toString(), // Ensure ID is string
        content: cleanCitationContent
      };

      this.$emit('editCitation', completeCitation);

      // Close the modal
      if (form.$emit) {
        form.$emit('closeModal');
      }
    }
  }

  public toggleCitationStatus() {
    if (this.activeCitation) {
      this.$emit('archiveCitation', this.citation);
    } else {
      this.$emit('unarchiveCitation', this.citation);
    }
  }

  public handleDeleteCitation() {
    this.$emit('deleteCitation', this.citation);
  }
}
